import { apiRequest } from "./api";

// Maps the wizard's preparer object (src/data/rates.js: blankPreparer) onto
// the backend's TaxPreparerRequest shape.
function toRequestBody(preparer) {
  const addressKind = preparer.kind === "foreign" ? "foreign" : "US";
  const body = {
    preparerName: preparer.name,
    ptin: preparer.ptin,
    credentialCode: preparer.credentials || null,
    email: preparer.email,
    phone: preparer.phone,
    ext: preparer.ext || "",
    firmEmployee: preparer.isFirmEmployee,
    firmName: preparer.isFirmEmployee ? preparer.firmName : "",
    firmEin: preparer.isFirmEmployee ? preparer.firmEin : "",
    addressKind,
  };
  if (addressKind === "US") {
    body.usAddress = { addressLine1: preparer.a1, city: preparer.city, state: preparer.state, zip: preparer.zip.replace(/\D/g, "") };
  } else {
    body.foreignAddress = {
      addressLine1: preparer.a1,
      addressLine2: preparer.a2 || "",
      city: preparer.city,
      province: preparer.state,
      country: preparer.country || preparer.countryName || "",
      postalCode: preparer.zip || "",
    };
  }
  return body;
}

// Maps a TaxPreparerResponse back onto the wizard's preparer object shape.
function fromResponseBody(response) {
  const isUs = response.addressKind === "US";
  const address = (isUs ? response.usAddress : response.foreignAddress) || {};
  return {
    id: response.id,
    name: response.preparerName || "",
    ptin: response.ptin || "",
    credentials: response.credentialCode || "",
    email: response.email || "",
    phone: response.phone || "",
    ext: response.ext || "",
    isFirmEmployee: !!response.firmEmployee,
    firmName: response.firmName || "",
    firmEin: response.firmEin || "",
    kind: isUs ? "us" : "foreign",
    // address.country is the isoCode (e.g. "CA"), not a display name - the
    // backend stores/returns it as-is (Return2290Assembler sends it straight
    // through as ForeignAddressType/CountryCd). Map it directly into
    // `country` so the Country <Select> can preselect it on edit; countryName
    // is just a display label, backfilled from the loaded country list below.
    country: isUs ? "US" : (address.country || ""),
    countryName: isUs ? "United States" : "",
    a1: address.addressLine1 || "",
    a2: isUs ? "" : (address.addressLine2 || ""),
    city: address.city || "",
    state: (isUs ? address.state : address.province) || "",
    zip: (isUs ? address.zip : address.postalCode) || "",
  };
}

// GET /tax-preparers -> [{ id, name }, ...] for the Business step's dropdown.
export async function listTaxPreparerOptions() {
  const data = await apiRequest("/tax-preparers");
  return Array.isArray(data) ? data : [];
}

export async function getTaxPreparer(preparerId) {
  const data = await apiRequest(`/tax-preparers/${preparerId}`);
  return fromResponseBody(data);
}

export async function createTaxPreparer(preparer) {
  const data = await apiRequest("/tax-preparers", {
    method: "POST",
    body: toRequestBody(preparer),
  });
  return fromResponseBody(data);
}

export async function updateTaxPreparer(preparerId, preparer) {
  const data = await apiRequest(`/tax-preparers/${preparerId}`, {
    method: "PUT",
    body: toRequestBody(preparer),
  });
  return fromResponseBody(data);
}

// Creates the preparer, or updates it in place if we already have its id
// from an earlier save this session (an independent record now, so there's
// no server-side duplicate check to fall back on).
export async function saveTaxPreparer(preparerId, preparer) {
  if (preparerId) {
    return updateTaxPreparer(preparerId, preparer);
  }
  return createTaxPreparer(preparer);
}

// DELETE /tax-preparers/{id} -> soft-deletes the preparer (backend sets
// deleted_at; it stops appearing in listTaxPreparerOptions(), but any
// business that already references it keeps its historical data).
export async function deleteTaxPreparer(preparerId) {
  await apiRequest(`/tax-preparers/${preparerId}`, { method: "DELETE" });
}
